import { useEffect } from "react";
import store from "../store";
import { selectIsAuthenticated } from "../store/slices/authSlice";
import {
  LOGGED_IN_APP_DESCRIPTION,
  LOGGED_IN_APP_TITLE,
} from "../constants/appDocumentTitle";

const DEFAULT_TITLE =
  "תוכנה לניהול קליניקה – יומן, תשלומים ותיק מטופל | CliniKit (קליניקיט)";
const DEFAULT_DESCRIPTION =
  "CliniKit (קליניקיט) – תוכנה לניהול קליניקה הכי קלה בישראל: יומן תורים עם כל האפשרויות, תזכורות WhatsApp, דפי תשלום וסליקת אשראי, חשבוניות, תיק מטופל וסיכומי AI. מאובטח ברמה רפואית, תמיכה אישית, מחיר משתלם. לכל קליניקה וסטודיו – 30 יום חינם.";
const BASE_URL = "https://clinikit.co.il";

function setMeta(attr, key, val) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", val);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function restoreDocumentMetaAfterSeoPage() {
  const loggedIn = selectIsAuthenticated(store.getState());
  const title = loggedIn ? LOGGED_IN_APP_TITLE : DEFAULT_TITLE;
  const description = loggedIn ? LOGGED_IN_APP_DESCRIPTION : DEFAULT_DESCRIPTION;
  document.title = title;
  setMeta("name", "description", description);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:url", BASE_URL + "/");
  setMeta("property", "og:type", "website");
  setCanonical(BASE_URL + "/");
}

const DEFAULT_ROBOTS = "index, follow, max-snippet:-1, max-image-preview:large";

/**
 * Marks the current page as noindex/nofollow without touching title/description.
 * Use on per-clinic / per-patient public pages (booking, confirm, sign, meet,
 * questionnaire) so search engines never surface therapist-specific links.
 */
export function useNoIndex() {
  useEffect(() => {
    setMeta("name", "robots", "noindex, nofollow");
    return () => setMeta("name", "robots", DEFAULT_ROBOTS);
  }, []);
}

/**
 * Sets page-level SEO meta tags and cleans them up on unmount.
 *
 * @param {Object} options
 * @param {string}  options.title       - Page title (appended with " | CliniKit" unless it already contains it)
 * @param {string}  options.description - Meta description
 * @param {string}  [options.path]      - URL path (e.g. "/login"), used for canonical & og:url
 * @param {string}  [options.ogType]    - og:type, defaults to "website"
 * @param {boolean} [options.noindex]   - If true, sets robots to "noindex, nofollow"
 */
export default function usePageSeo({
  title,
  description,
  path = "/",
  ogType = "website",
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title.includes("CliniKit")
      ? title
      : `${title} | CliniKit`;
    const fullUrl = `${BASE_URL}${path}`;

    document.title = fullTitle;

    setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", fullUrl);
    setMeta("property", "og:type", ogType);
    setCanonical(fullUrl);

    if (noindex) {
      setMeta("name", "robots", "noindex, nofollow");
    }

    return () => {
      restoreDocumentMetaAfterSeoPage();

      if (noindex) {
        setMeta(
          "name",
          "robots",
          "index, follow, max-snippet:-1, max-image-preview:large"
        );
      }
    };
  }, [title, description, path, ogType, noindex]);
}
